// Composer Studio — 편집 툴바 (Phase C)
//
// 캔버스 상단 가로 바. 전역 편집 액션의 홈:
//   [↶ 실행취소][↷ 다시실행] │ [⊞ 그룹으로 묶기] │ [그룹 해제][그룹⇄스택 전환]
//
// 문맥 활성화:
//   - undo/redo         : canUndo / canRedo
//   - 묶기(그룹·스택)    : selectionCount>=2 && canGroup(형제 2개+ 선택)
//   - 해제 / 전환        : isContainer(group·stack 단일 선택)
//
// EditorPage state에 직접 의존하지 않고 개별 값+콜백만 props로 받는다(재사용/테스트 용이).
// 줌·가운데정렬 UI는 이 툴바로 옮기지 않는다(캔버스 뷰포트에 병존 — 결정 기록 2026-07-02 4항).

interface EditorToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** 현재 다중선택 노드 개수(0=없음, 1=단일, 2+=묶기 후보). */
  selectionCount: number;
  /** 선택 노드들이 같은 부모의 형제라 묶기 가능한지. */
  canGroup: boolean;
  /** 단일 선택 노드가 group/stack 컨테이너인지. */
  isContainer: boolean;
  /** 컨테이너 종류(전환 버튼 라벨용). 컨테이너 아니면 null. */
  containerKind: 'group' | 'stack' | null;
  onGroup: (kind: 'group' | 'stack') => void;
  onUngroup: () => void;
  /** 단일 선택 노드가 부모 컨테이너(group/stack 또는 branch 슬롯) 밖으로 승격 가능한지. */
  canMoveOut: boolean;
  onMoveOut: () => void;
  onToggleKind: () => void;
  /** 패딩 시각화 오버레이 표시 여부(보기 토글 — 우측 정렬). */
  showPadding: boolean;
  onToggleShowPadding: (next: boolean) => void;
  /** 배지(바인딩🔗/계산ƒ/집계∑) 오버레이 표시 여부("배지 보기" 토글). */
  showBadges: boolean;
  onToggleShowBadges: (next: boolean) => void;
  /** 캔버스 선택 방식 — 'isolation'(그룹 우선) | 'deepest'(최심부 우선). 체크=isolation. */
  selectionMode: 'isolation' | 'deepest';
  onToggleSelectionMode: (next: 'isolation' | 'deepest') => void;
  /**
   * 🔗 데이터 연결 후보 제안 모달 열기 — body 바인딩을 편집하는 도구라 보고서 탭 툴바 소관
   * (2026-07-27 결정: 헤더=문서 수명주기 / 탭 툴바=그 탭을 편집하는 도구).
   */
  onOpenBindingSuggest: () => void;
  bindingSuggestDisabled?: boolean;
}

// admin 톤 공통 버튼 클래스(작은 버튼, text-xs, gray/brand 계열 — 헤더/속성패널과 일관).
const BTN_BASE =
  'inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border transition-colors ' +
  'disabled:cursor-not-allowed';
const BTN_GRAY =
  BTN_BASE +
  ' border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:bg-white';
const BTN_BRAND =
  BTN_BASE +
  ' border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100 disabled:text-gray-300 disabled:border-gray-200 disabled:bg-white';

const DIVIDER = <span className="mx-0.5 h-5 w-px bg-gray-300" aria-hidden />;

/**
 * 캔버스 상단 고정 편집 툴바. 캔버스 컨테이너 최상단에 sticky로 얹혀 스크롤과 무관하게 노출된다.
 */
export function EditorToolbar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  selectionCount,
  canGroup,
  isContainer,
  containerKind,
  onGroup,
  onUngroup,
  canMoveOut,
  onMoveOut,
  onToggleKind,
  showPadding,
  onToggleShowPadding,
  showBadges,
  onToggleShowBadges,
  selectionMode,
  onToggleSelectionMode,
  onOpenBindingSuggest,
  bindingSuggestDisabled,
}: EditorToolbarProps) {
  const groupEnabled = selectionCount >= 2 && canGroup;
  // 비활성 안내에도 단축키를 병기 — 대부분 비활성 상태에서 hover하므로 단축키가 안 보이는 문제 방지.
  const groupDisabledTitle = '형제 노드 2개 이상 선택 시 사용할 수 있습니다 (Ctrl+G)';
  const containerDisabledTitle = '그룹/스택 컨테이너를 선택하면 사용할 수 있습니다';
  const toggleLabel = containerKind === 'stack' ? '그룹으로 전환' : '스택으로 전환';

  return (
    <div
      className="sticky top-0 z-10 flex flex-wrap items-center gap-1.5 rounded-card border border-gray-200 bg-white px-2 py-1.5 shadow-sm"
      role="toolbar"
      aria-label="편집 툴바"
    >
      {/* 실행취소 / 다시실행 */}
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        title="실행 취소 (Ctrl+Z)"
        className={BTN_GRAY}
      >
        ↶ 실행취소
      </button>
      <button
        type="button"
        onClick={onRedo}
        disabled={!canRedo}
        title="다시 실행 (Ctrl+Shift+Z 또는 Ctrl+Y)"
        className={BTN_GRAY}
      >
        ↷ 다시실행
      </button>

      {DIVIDER}

      {/* 묶기 — 그룹만(스택이 필요하면 묶은 뒤 전환. 스택 직접 묶기 버튼은 진입점 중복이라 제거, 2026-07-02) */}
      <button
        type="button"
        onClick={() => onGroup('group')}
        disabled={!groupEnabled}
        title={groupEnabled ? '선택한 노드를 그룹으로 묶기 (Ctrl+G)' : groupDisabledTitle}
        className={BTN_BRAND}
      >
        ⊞ 그룹으로 묶기
      </button>

      {DIVIDER}

      {/* 해제 / 전환 */}
      <button
        type="button"
        onClick={onUngroup}
        disabled={!isContainer}
        title={
          isContainer
            ? '컨테이너를 풀어 자식을 상위로 올리기 (Ctrl+Shift+G)'
            : `${containerDisabledTitle} (Ctrl+Shift+G)`
        }
        className={BTN_GRAY}
      >
        그룹 해제
      </button>
      <button
        type="button"
        onClick={onMoveOut}
        disabled={!canMoveOut}
        title={
          canMoveOut
            ? '선택 노드를 부모 컨테이너 밖으로 한 단계 내보내기'
            : '그룹/스택 또는 분기 슬롯 안의 노드를 단일 선택하면 사용할 수 있습니다'
        }
        className={BTN_GRAY}
      >
        그룹(스택) 밖으로
      </button>
      <button
        type="button"
        onClick={onToggleKind}
        disabled={!isContainer}
        title={isContainer ? `${toggleLabel} (구조 유지, 종류만 변경)` : containerDisabledTitle}
        className={BTN_GRAY}
      >
        {isContainer ? toggleLabel : '그룹⇄스택 전환'}
      </button>

      {DIVIDER}

      {/* 데이터 연결 제안 — body 바인딩 편집 도구(선택 무관, 문서 단위) */}
      <button
        type="button"
        onClick={onOpenBindingSuggest}
        disabled={bindingSuggestDisabled}
        title="AI가 입력 필드와 시스템 데이터(자동 채움 출처)의 연결을 제안합니다"
        className={BTN_GRAY}
      >
        🔗 데이터 연결 제안
      </button>

      {/* 선택 방식 토글(우측 정렬) — 체크: 그룹/컨테이너를 단위로 승격 선택(안으로는 더블클릭 드릴).
          해제: 클릭한 가장 안쪽 요소를 바로 선택하는 이전 방식(재클릭=편집, Esc=부모로). */}
      <label
        className="ml-auto flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none"
        title={
          '체크: 그룹/컨테이너를 단위로 승격 선택 — 안으로 들어가려면 더블클릭(드릴), Esc로 상위 그룹으로.\n' +
          '해제: 클릭한 가장 안쪽 요소를 바로 선택하는 이전 방식 — 선택된 글자 재클릭=편집, 더블클릭=편집, Esc=부모로.'
        }
      >
        <input
          type="checkbox"
          checked={selectionMode === 'isolation'}
          onChange={(e) => onToggleSelectionMode(e.target.checked ? 'isolation' : 'deepest')}
        />
        그룹 우선 선택
      </label>

      {/* 보기 토글(우측) — 편집 보조 오버레이 표시 여부 */}
      <label
        className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none"
        title="노드/셀 패딩 영역을 캔버스에 색으로 표시"
      >
        <input
          type="checkbox"
          checked={showPadding}
          onChange={(e) => onToggleShowPadding(e.target.checked)}
        />
        패딩 보기
      </label>
      <label
        className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none"
        title="바인딩(🔗)·계산(ƒ)·집계(∑) 배지를 캔버스에 표시"
      >
        <input
          type="checkbox"
          checked={showBadges}
          onChange={(e) => onToggleShowBadges(e.target.checked)}
        />
        배지 보기
      </label>
    </div>
  );
}
